import React, { useState, useContext, useCallback } from 'react'
import { AuthContext } from '@/layout/Auth'
import { styled } from '@mui/material/styles'
import { TextField, Stack, Alert } from '@mui/material'
import { FormCard, ListWrapper, ScrollY, Row } from '../components'
import { CrateAPITokenInfo, PermissionInfo } from '@/types'
import Form, { FormItem } from '@/components/Form'
import Text from '@/components/Text'
import Button, { ConfirmButton } from '@/components/Button'
import Select from '@/components/Select'
import { secondaryText } from '@/constant'
import TimePicker from '@/components/TimePicker'
import { createApiToken, getDetailPermissions } from '@/api'
import { useQuery, useMutation } from '@tanstack/react-query'
import { confirm } from '@/components/Modal'
import dayjs from 'dayjs'
import { Message } from '@cx/ui'

interface CrateAPITokenFormInfo extends Omit<CrateAPITokenInfo, 'expired_at'> {
  expired_at: number
}

export const TokenContent = styled(Text)(() => ({
  letterSpacing: '4px',
  width: '100%',
  fontSize: '14px',
  lineHeight: '32px',
  padding: '0px 16px',
  backgroundColor: '#F7F7F780',
  overflowWrap: 'anywhere',
}))

export interface TokenCardProps {
  value: CrateAPITokenFormInfo
  onChange: (info: CrateAPITokenFormInfo) => void
  permissionOptions: PermissionInfo[]
  validating?: boolean
  onErrorChange?: (hasError: boolean) => void
}

export const TokenCard: React.FC<TokenCardProps> = (props) => {
  const { permissionOptions, validating, value, onChange, onErrorChange } = props

  // const renderSelecOptions = useCallback(
  //   (option: SelectOptionProps) => (
  //     <Text sx={{ display: "flex", width: "100%", alignItems: "center" }}>
  //       {option?.label?.split("-")[0]}
  //       <Text size="xs" sx={{ color: leastText }}>
  //         {option?.label?.split("-")[1]}
  //       </Text>
  //     </Text>
  //   ),
  //   []
  // );

  return (
    <FormCard sx={{ gap: '30px' }}>
      <Form
        sx={{
          width: '100%',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: '16px',
        }}
        initialValue={value}
        // @ts-ignore
        onFormValueChange={onChange}
        // @ts-ignore
        onValidate={onErrorChange}
        validating={validating}
      >
        <FormItem
          sx={{ width: '100%', gridColumn: '1/-1' }}
          label='name'
          text='名称'
          rules={{
            required: true,
            errorMessage: '请输入API Token的名称',
            validator: (value: string) => value.length > 0,
          }}
        >
          <TextField fullWidth />
        </FormItem>
        <FormItem
          sx={{ gridColumn: 1 }}
          label='perms'
          text='权限'
          rules={{
            required: true,
            errorMessage: '请选择API Token的权限',
            validator: (value: Array<number>) => value.length > 0,
          }}
        >
          <Select
            multiple={true}
            options={
              permissionOptions?.map((option) => ({
                value: option?.id,
                label: [option?.scope, option?.name]?.join(' - '),
              })) ?? []
            }
            // renderOption={renderSelecOptions}
          />
        </FormItem>
        <FormItem
          label='expired_at'
          text='过期时间'
          rules={{
            errorMessage: '请输入过期时间',
            validator: (value: number) => !!value,
          }}
        >
          <TimePicker />
        </FormItem>
      </Form>
    </FormCard>
  )
}

export interface TokenCreateProps {
  onCancel: () => void
}

const HightlightText = styled(Text)(() => ({
  fontSize: '14px',
  weight: 'bold',
}))

const defaultExpireTime = () => {
  const date = new Date()
  date.setMonth(date.getMonth() + 1)
  return date.getTime()
}

const TokenCreate: React.FC<TokenCreateProps> = (props) => {
  const { onCancel } = props
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [tokeninfo, setTokeninfo] = useState<CrateAPITokenFormInfo>({
    name: '',
    perms: [],
    expired_at: defaultExpireTime(),
  })
  const [hasError, setHasError] = useState<boolean>(false)
  const [validating, setValidation] = useState<boolean>(false)

  const { data: res } = useQuery(
    ['getDetailPermissions', orgInfo?.id],
    () =>
      getDetailPermissions({
        // @ts-ignore
        id: orgInfo?.id,
        query: { size: 100 },
      }),
    {
      refetchOnMount: true,
      enabled: !!orgInfo?.id,
    },
  )

  const createApiTokenMutation = useMutation(createApiToken, {
    onSuccess: (res) => {
      setValidation(false)
      Message.success('生成API Token成功')
      confirm({
        title: '复制API Token',
        content: (
          <Stack sx={{ gap: '8px' }}>
            <Alert severity='info'>本页面关闭后，百川云平台将不再存储和显示 API Token，请妥善保存。</Alert>
            <TokenContent>{res.token}</TokenContent>
          </Stack>
        ),
        // @ts-ignore
        onClose: null,
        // @ts-ignore
        onConfirm: null,
        width: 'auto',
        hideClose: true,
        clipboard: {
          text: res.token,
          onCopy: () => Message.success('已复制token至剪贴板'),
        },
        closeAfterClose: true,
      })
      onCancel()
    },
  })

  const handleCreate = useCallback(() => {
    setValidation(true)
    if (!hasError) {
      const data = {
        org_id: orgInfo?.id,
        ...tokeninfo,
        expired_at: dayjs(tokeninfo.expired_at).format('YYYY-MM-DD HH:mm:ss'),
      }
      // @ts-ignore
      createApiTokenMutation.mutate(data)
    }
  }, [createApiTokenMutation, hasError, orgInfo?.id, tokeninfo])

  const handleChangeValidate = useCallback((validateState: boolean) => {
    setHasError(validateState)
  }, [])

  return (
    <Stack sx={{ height: '100%', gap: '30px' }}>
      <Stack sx={{ gap: '8px' }}>
        <Text weight='bold'>生成API Token</Text>
        <Text
          sx={{
            display: 'flex',
            fontSize: '14px',
            alignItems: 'center',
            color: secondaryText,
          }}
        >
          在空间
          <HightlightText weight='bold' sx={{ margin: '0 4px' }}>
            {orgInfo?.name}
          </HightlightText>
          中生成新的API Token
        </Text>
      </Stack>
      <ScrollY sx={{ flex: 1 }}>
        <ListWrapper sx={{ gap: '30px' }}>
          <TokenCard
            validating={validating}
            onChange={setTokeninfo}
            value={tokeninfo}
            permissionOptions={res?.data ?? []}
            onErrorChange={handleChangeValidate}
          />
        </ListWrapper>
      </ScrollY>
      <Row sx={{ justifyContent: 'flex-end', gap: '16px' }}>
        <Button type='secondary' onClick={onCancel}>
          取消
        </Button>
        <ConfirmButton onClick={handleCreate}>生成API Token</ConfirmButton>
      </Row>
    </Stack>
  )
}

export default TokenCreate
